import { createContext, memo, useCallback, useContext, useMemo, useReducer, useRef } from "react";
import { InternalState } from "./state-models";
import { getLocationItemFromState } from "./state-url-management";
import { SourceItem } from "../shared/models/source-item.model";
import _ from "lodash";
import { useFilterContext } from "./FilterContext";
import { useFileContext } from "./FileContext";

export enum InternalActionName {
    SET_STORED_ITEM = "SET_STORED_ITEM",
    SET_STORED_ARRAY = "SET_STORED_ARRAY",
};

export type InternalStateAction =
    | { type: InternalActionName.SET_STORED_ITEM; payload: InternalState["storedItems"] }
    | { type: InternalActionName.SET_STORED_ARRAY; payload: InternalState["storedArrays"] }

const internalStateReducer = (state: InternalState, action: InternalStateAction): InternalState => {
    switch (action.type) {
        case InternalActionName.SET_STORED_ITEM:
            const newStoredItems = { ...(state.storedItems || {}), ...action.payload };
            return _.isEqual(newStoredItems, state.storedItems) ? state : { ...state, storedItems: newStoredItems };
        case InternalActionName.SET_STORED_ARRAY:
            const newStoredArrays = { ...(state.storedArrays || {}), ...action.payload };
            return _.isEqual(newStoredArrays, state.storedArrays) ? state : { ...state, storedArrays: newStoredArrays };
    }
};

type InternalContextType = {
    internalState: InternalState;
    actions: Record<InternalActionName, (...args: any[]) => void>;
} | null;

type InternalDispatchContextType = {
    dispatch: React.Dispatch<InternalStateAction>;
    createBranch: (initialState: InternalState) => React.ComponentType<{ children: React.ReactNode }>;
};

export const InternalStateContext = createContext<InternalContextType | undefined>(undefined);
export const InternalDispatchContext = createContext<InternalDispatchContextType | undefined>(undefined);

type InternalStateProviderProps = {
    children: React.ReactNode;
    initialState?: InternalState;
    parentContext?: InternalContextType;
};

// Root provider that calculates locationItem once and passes it down
export const InternalStateRootProvider = memo(({ children, initialState = {} }: { children: React.ReactNode; initialState?: InternalState }) => {
    const locationItem = useLocationItem();

    const rootInitialState = useMemo(() => ({
        ...initialState,
        storedItems: {
            ...initialState.storedItems,
            ...(locationItem ? { _location: locationItem } : {})
        }
    }), [initialState, locationItem]);

    return (
        <InternalStateProvider initialState={rootInitialState}>
            {children}
        </InternalStateProvider>
    );
});

export const InternalStateProvider = memo(({ children, initialState = {}, parentContext }: InternalStateProviderProps) => {
    const memoizedInitialState = useMemo(() => initialState, [initialState]);
    const [localState, dispatch] = useReducer(internalStateReducer, memoizedInitialState);

    // Get locationItem from parent context or calculate it at root level
    const locationItem = parentContext?.internalState?.storedItems?._location || null;

    const mergedState = useMemo(() => {
        const storedItems = {
            ...(parentContext?.internalState?.storedItems || {}),
            ...(localState.storedItems || {}),
            ...(locationItem ? { _location: locationItem } : {})
        };
        const storedArrays = {
            ...(parentContext?.internalState?.storedArrays || {}),
            ...(localState.storedArrays || {}),
        };
        return { ...(parentContext?.internalState), ...localState, storedItems: storedItems, storedArrays: storedArrays };
    }, [parentContext?.internalState, localState, locationItem]);

    // Create actions for updating the state
    // These actions are used to update the state in the current state branch
    const actions: Record<InternalActionName, (...args: any[]) => void> = useMemo(() => {
        return {
            [InternalActionName.SET_STORED_ITEM]: (props: Record<string, SourceItem>) => dispatch({ type: InternalActionName.SET_STORED_ITEM, payload: props }),
            [InternalActionName.SET_STORED_ARRAY]: (props: Record<string, Array<SourceItem>>) => dispatch({ type: InternalActionName.SET_STORED_ARRAY, payload: props }),
        };
    }, [dispatch]);


    // Use a ref to store the current mergedState so createBranch doesn't recreate unnecessarily
    const mergedStateRef = useRef(mergedState);
    mergedStateRef.current = mergedState;

    // Create a stable createBranch function that doesn't recreate when mergedState changes
    const createBranch = useCallback((branchInitialState: InternalState) => {
        return ({ children }: { children: React.ReactNode }) => (
            <InternalStateProvider
                initialState={branchInitialState}
                parentContext={{ internalState: mergedStateRef.current, actions }}
            >
                {children}
            </InternalStateProvider>
        );
    }, [actions]); // Only depend on actions, not mergedState

    return (
        <InternalStateContext.Provider value={{ internalState: mergedState, actions }}>
            <InternalDispatchContext.Provider value={{ dispatch, createBranch }}>
                {children}
            </InternalDispatchContext.Provider>
        </InternalStateContext.Provider>
    );
});

export const useInternalDispatch = () => {
    const context = useContext(InternalDispatchContext);
    if (!context) {
        throw new Error("useInternalDispatch must be used within a InternalStateProvider");
    }
    return context;
};

export const useInternalContext = () => {
    const context = useContext(InternalStateContext);
    if (!context) {
        throw new Error("useInternalContext must be used within a InternalStateProvider");
    }
    return context;
};

// Selector for getting the createStateBranch function
export const useCreateInternalStateBranch = () => {
    const context = useContext(InternalDispatchContext);
    if (!context) {
        throw new Error('useInternalCreateStateBranch must be used within a InternalStateProvider');
    }
    return useMemo(() => context.createBranch, [context.createBranch]);
};

export const useInternalState = () => {
    const { internalState } = useInternalContext();
    return internalState;
};

// Selector for state modifying actions
export const useInternalAction = (actionName: InternalActionName): ((...args: any[]) => void) => {
    const context = useContext(InternalStateContext);
    if (!context) {
        throw new Error('useInternalAction must be used within a InternalStateProvider');
    }
    return useCallback((...args: any[]) => context.actions[actionName](...args), [context.actions, actionName]);
};

export const useContentType = () => {
    const { internalState: mergedState } = useInternalContext();
    return useMemo(() => mergedState.contentType, [mergedState.contentType]);
};

export const useCssClassRules = () => {
    const { internalState: mergedState } = useInternalContext();
    return useMemo(() => mergedState.cssClassRules, [mergedState.cssClassRules]);
};

export const useInjectElementRules = () => {
    const { internalState: mergedState } = useInternalContext();
    return useMemo(() => mergedState.injectElementRules, [mergedState.injectElementRules]);
};

export const useCallbacks = () => {
    const { internalState: mergedState } = useInternalContext();
    return useMemo(() => mergedState.callbacks, [mergedState.callbacks]);
};

export const useIsValidForm = () => {
    const { internalState: mergedState } = useInternalContext();
    return useMemo(() => mergedState.isValidForm, [mergedState.isValidForm]);
};

export const useStoredArrays = () => {
    const { internalState: mergedState } = useInternalContext();
    return useMemo(() => mergedState.storedArrays, [mergedState.storedArrays]);
};

export const useStoredArray = (store: string) => {
    const { internalState: mergedState } = useInternalContext();
    return useMemo(() => mergedState.storedArrays?.[store], [mergedState.storedArrays, store]);
};

// Selector for getting the current SourceItem representing the location
export const useLocationItem = () => {
    const { filterState } = useFilterContext();
    const { fileState } = useFileContext();
    const prevLocationItemRef = useRef<SourceItem | null>(null);

    const locationItem = useMemo(() => {
        const combinedState = { ...filterState, ...fileState };
        const newLocationItem = getLocationItemFromState(combinedState);

        // Only update if the location item has actually changed
        if (_.isEqual(newLocationItem, prevLocationItemRef.current)) {
            return prevLocationItemRef.current;
        }

        prevLocationItemRef.current = newLocationItem;
        return newLocationItem;
    }, [filterState, fileState]);

    return locationItem;
};

export const useChatbotHistory = () => {
    const { internalState: mergedState } = useInternalContext();
    return useMemo(() => mergedState.chatbotHistory, [mergedState.chatbotHistory]);
};

// Selector for stored items
export const useStoredItems = () => {
    const { internalState: mergedState } = useInternalContext();
    return useMemo(() => {
        const storedItems = mergedState.storedItems || {};
        return storedItems;
    }, [mergedState.storedItems]);
};

// Selector for a specific stored item
export const useStoredItem = (store: string) => {
    const { internalState: mergedState } = useInternalContext();
    return useMemo(() => {
        return mergedState.storedItems?.[store];
    }, [mergedState.storedItems, store]);
};


























